<template>
  <div
    ref="rootEl"
    class="grid max-w-[26.5rem] grid-cols-6 gap-2"
    role="group"
    :aria-label="props.context?.label || 'Verification Code'"
    data-testid="otp"
  >
    <input
      v-for="index in digits"
      :key="index"
      class="max-w-16 rounded-md border border-fp-gray-3 text-3xl text-fp-gray-5 text-center ring-offset-0 ring-0 focus:ring-primary focus:border-primary"
      maxlength="1"
      :class="[
        props.context?.classes?.size === 'formkit-size small' ? 'h-16' : 'h-24',
        props.context?.classes?.input,
      ]"
      :value="tmp[index - 1] || ''"
      :aria-label="`${props.context?.label || 'Verification Code'} digit ${index}`"
      @input="handleInput(index - 1, $event)"
      @focus="handleFocus"
      @paste="handlePaste"
    />
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue'
import type { FormKitFrameworkContext } from '@formkit/core'

// Adapted from the OTP example given in https://formkit.com/guides/create-a-custom-input (28/01/2025)

const props = defineProps<{
  context: FormKitFrameworkContext
}>()

const digits = Number(props.context.digits)
const tmp = ref(props.context.value || '')
const rootEl = ref<HTMLElement | null>(null)

onMounted(() => {
  if (
    props.context.attrs?.autofocus == null &&
    !props.context.node.props.autofocus
  ) {
    return
  }
  nextTick(() => {
    rootEl.value?.querySelector('input')?.focus()
  })
})

/**
 * Handle input, advancing or retreating focus.
 */
function handleInput(index: number, e: Event) {
  const prev = tmp.value

  const eventTarget = e.target as HTMLInputElement

  if (tmp.value.length <= index) {
    // If this is a new digit
    tmp.value = '' + tmp.value + eventTarget.value
  } else {
    // If this digit is in the middle somewhere, cut the string into two
    // pieces at the index, and insert our new digit in.
    tmp.value =
      '' +
      tmp.value.substr(0, index) +
      eventTarget.value +
      tmp.value.substr(index + 1)
  }

  // Get all the digit inputs
  const inputs = eventTarget.parentElement?.querySelectorAll('input') || []

  if (index < digits - 1 && tmp.value.length >= prev.length) {
    // If this is a new input and not at the end, focus the next input
    inputs[index + 1]?.focus()
  } else if (index > 0 && tmp.value.length < prev.length) {
    // in this case we deleted a value, focus backwards
    inputs[index - 1]?.focus()
  }

  if (tmp.value.length === digits) {
    // If our input is complete, commit the value.
    props.context.node.input(tmp.value)
  } else if (tmp.value.length < digits && props.context.value !== '') {
    // If our input is incomplete, it should have no value.
    props.context.node.input('')
  }
}

/**
 * On focus, select the text in our input.
 */
function handleFocus(e: Event) {
  const eventTarget = e.target as HTMLInputElement
  eventTarget.select()
}

/**
 * Handle the paste event.
 */
function handlePaste(e: ClipboardEvent) {
  const paste = e.clipboardData?.getData('text')
  if (typeof paste === 'string') {
    // If it is the right length, paste it.
    tmp.value = paste.slice(0, digits)
    const inputs = (e.target as HTMLElement).querySelectorAll('input')
    // Focus on the last character
    inputs[tmp.value.length - 1]?.focus()
  }
}
</script>

<style scoped></style>
